import { FeatureFlags, useFeature } from '@melio/platform-feature-flags';
import { usePlans, useSubscriptionMe } from '@melio/subscriptions-api';
import { useEffect, useMemo, useState } from 'react';

import { SubscriptionContext } from './SubscriptionContext';
import { SubscriptionContextualPaywallProps, SubscriptionProviderProps, SubscriptionSuccessModalProps } from './types';

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ fallback, children }) => {
  const [isPlatformSubscriptionsEnabled] = useFeature(FeatureFlags.PlatformSubscriptions, false);
  const {
    data: subscription,
    isFetching: isFetchingMySubscription,
    isError: isMySubscriptionError,
    error: mySubscriptionError,
  } = useSubscriptionMe({
    enabled: isPlatformSubscriptionsEnabled,
  });

  const {
    data: plans,
    isFetching: isFetchingPlans,
    error: plansError,
  } = usePlans({
    enabled: isPlatformSubscriptionsEnabled,
  });

  const [isOnboarding, setIsOnboarding] = useState(false);
  const [successModalProps, setSuccessModalProps] = useState<SubscriptionSuccessModalProps | null>(null);
  const [paywallProps, setPaywallProps] = useState<SubscriptionContextualPaywallProps | null>(null);

  const hasNoSubscription = isMySubscriptionError && mySubscriptionError?.code === '404';

  useEffect(() => {
    if (isPlatformSubscriptionsEnabled && hasNoSubscription) {
      setIsOnboarding(true);
    }
  }, [isPlatformSubscriptionsEnabled, hasNoSubscription]);

  const value = useMemo(
    () => ({
      plans,
      subscription,
      onboarding: {
        hasNoSubscription,
        isOnboarding,
        completeOnboarding: () => setIsOnboarding(false),
      },
      successModal: {
        successModalProps,
        showSuccessModal: setSuccessModalProps,
        hideSuccessModal: () => setSuccessModalProps(null),
      },
      contextualPaywall: {
        paywallProps,
        showPaywall: setPaywallProps,
        hidePaywall: () => setPaywallProps(null),
      },
    }),
    [
      plans,
      subscription,
      hasNoSubscription,
      isOnboarding,
      setIsOnboarding,
      successModalProps,
      setSuccessModalProps,
      paywallProps,
      setPaywallProps,
    ]
  );

  if ((isFetchingMySubscription && !isMySubscriptionError) || (isFetchingPlans && !plansError)) {
    return <>{fallback}</>;
  }

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
};
